import React, { useState, useEffect, useRef, SyntheticEvent } from 'react'
import styled from 'styled-components'
import { range, round, max, floor } from 'lodash'
import { ICapture, ISegment } from '../shared/types'
import { giphyWhite, giphyYellow, giphyBlack, giphyDarkestGrey } from '@giphy/js-brand'
import CropPreview from '../shared/components/crop-preview'
import SegmentPreview from '../shared/components/segment-preview'
import MergedPill from '../shared/icons/merged-pill'
import { EllipsisDropdown } from '@giphy/react-giphy-brand'
import useWindowScroll from 'react-use/lib/useWindowScroll'
import useWindowSize from 'shared/hooks/window-size'

const CELL_WIDTH = 250
const CELL_PADDING = 10
const GIFS_PER_ROW = 4

const Icon = styled.div`
    display: none;
    color: ${giphyWhite};
    position: absolute;
    width: 30px;
    height: 30px;
    text-align: center;
    line-height: 30px;
    text-shadow: 0px 2px 10px rgba(0, 0, 0, 0.23);
`

const EditButton = styled(Icon)`
    top: 8px;
    left: 8px;
    cursor: pointer;
`

const EllipsisButton = styled(Icon)`
    bottom: 8px;
    right: 8px;
    cursor: pointer;
`

const EllipsisItem = styled.div`
    cursor: pointer;
`

const CheckButton = styled(Icon)`
    top: 8px;
    right: 8px;
    cursor: pointer;
`

const MergedIcon = styled(MergedPill)`
    position: absolute;
    top: 10px;
    left: 10px;
`

const GridCellContainer = styled.div<{ width; height }>`
    background-color: ${giphyDarkestGrey};
    width: ${props => props.width}px;
    height: ${props => props.height}px;
    display: inline-block;
    vertical-align: top;
    position: relative;
    margin: 0px ${CELL_PADDING / 2}px ${CELL_PADDING}px ${CELL_PADDING / 2}px;
    cursor: pointer;

    &:hover {
        ${EditButton}, ${EllipsisButton}, ${CheckButton} {
            display: block;
        }

        ${MergedIcon} {
            display: none;
        }
    }
`

const SelectedBorder = styled.div`
    border: 5px solid #00cffc;
    box-sizing: border-box;
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
`

const SelectedIcon = styled.div`
    width: 35px;
    height: 35px;
    position: absolute;
    top: 0;
    right: 0;
    background-color: ${giphyYellow};
    color: ${giphyBlack};
    text-align: center;
    line-height: 38px;
`

const CaptionWatermarkImage = styled.img`
    position: absolute;
    bottom: 0;
    left: 0;
    text-align: center;
    width: 100%;
    padding: 10px;
`

const DurationLabel = styled.div`
    position: absolute;
    bottom: 10px;
    left: 10px;
    background: white;
    border-radius: 2px;
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.5);
    color: ${giphyBlack};
    font-family: NexaBlack;
    font-size: 13px;
    letter-spacing: 0px;
    text-align: center;
    padding: 1px 5px;
`

type CellProps = {
    capture: ICapture
    segment: ISegment
    isSelected: boolean
    onClick: (e: SyntheticEvent<HTMLElement, Event>) => void
    onEdit: () => void
    onMerge: () => void
    onDelete: () => void
    onDuplicate: () => void
    onRevert: () => void
    onMouseEnter: () => void
    onMouseLeave: () => void
    showVideo?: boolean
    soundEnabled?: boolean
    inView?: boolean
}

const GridCell = ({
    capture,
    segment,
    isSelected,
    onClick,
    onEdit,
    onMerge,
    onDelete,
    onDuplicate,
    onRevert,
    onMouseEnter,
    onMouseLeave,
    showVideo = false,
    soundEnabled = false,
    inView = true,
}: CellProps) => {
    const onEditClicked = evt => {
        evt.stopPropagation()
        onEdit()
    }

    return (
        <GridCellContainer
            width={CELL_WIDTH}
            height={(CELL_WIDTH / capture.width) * capture.height}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {inView && (
                <>
                    <SegmentPreview segment={segment} showVideo={showVideo} soundEnabled={soundEnabled} />

                    {segment.editParams && <CaptionWatermarkImage src={segment.editParams.captionWatermark} />}

                    <CropPreview
                        originalWidth={capture.width}
                        originalHeight={capture.height}
                        targetAspectRatio={segment.editParams ? segment.editParams.aspectRatio : 'original'}
                        renderedWidth={CELL_WIDTH}
                    />

                    {isSelected && (
                        <>
                            <SelectedBorder />
                            <SelectedIcon className="ss-check" />
                        </>
                    )}

                    <EditButton className="ss-write" onClick={onEditClicked} />

                    {segment.editParams && segment.editParams.isMerged && <MergedIcon />}

                    <DurationLabel>{`${(segment.editParams
                        ? segment.editParams.trimmedEndTime - segment.editParams.trimmedStartTime
                        : segment.duration
                    ).toFixed(1)}s`}</DurationLabel>

                    <EllipsisButton onClick={evt => evt.stopPropagation()}>
                        <EllipsisDropdown isHover={true} extendDirectionUp={true}>
                            <EllipsisItem onClick={onDelete}>Delete</EllipsisItem>
                            <EllipsisItem onClick={onDuplicate}>Duplicate</EllipsisItem>
                            <EllipsisItem onClick={onMerge}>Merge Clip</EllipsisItem>
                            <EllipsisItem onClick={onRevert}>Revert to Original</EllipsisItem>
                        </EllipsisDropdown>
                    </EllipsisButton>
                </>
            )}
        </GridCellContainer>
    )
}

type GridProps = {
    capture: ICapture
    segments: ISegment[]
    selected: number[]
    segmentClicked: Function
    editClicked: Function
    mergeClicked: Function
    deleteClicked: Function
    duplicateClicked: Function
    revertClicked: Function
    soundEnabled?: boolean
}

const Grid = ({
    capture,
    segments,
    selected,
    segmentClicked,
    editClicked,
    mergeClicked,
    deleteClicked,
    duplicateClicked,
    revertClicked,
    soundEnabled = false,
}: GridProps) => {
    const [hoveredIndex, setHoveredIndex] = useState<number>()
    const [inViewRows, setInViewRows] = useState<number[]>([])

    const containerRef = useRef<HTMLDivElement>(null)

    const { y: windowScrollY } = useWindowScroll()
    const { innerHeight: windowHeight } = useWindowSize()

    useEffect(() => {
        if (containerRef.current) {
            const rowHeight = (CELL_WIDTH / capture.width) * capture.height + CELL_PADDING
            const windowHeight = document.documentElement.clientHeight
            const totalRowsDisplayable = round(windowHeight / rowHeight)
            const topOffset = max([windowScrollY - containerRef.current!.offsetTop, 0])!

            const startRow = floor(topOffset / rowHeight)
            const endRow = startRow + totalRowsDisplayable

            setInViewRows(range(startRow, endRow))
        }
    }, [windowScrollY, windowHeight])

    const filteredSegments = segments
        .filter(s => !s.editParams.displayHidden)
        .sort((a: ISegment, b: ISegment) => {
            const orderA = a.editParams.displayOrder || a.index
            const orderB = b.editParams.displayOrder || b.index
            return orderA - orderB
        })

    return (
        <div ref={containerRef}>
            {filteredSegments.map((s, i) => {
                const row = floor(i / GIFS_PER_ROW)
                const isSelected = selected.includes(s.index)
                return (
                    <GridCell
                        key={s.index}
                        capture={capture}
                        segment={s}
                        isSelected={isSelected}
                        onClick={e => segmentClicked(e, s.index)}
                        onEdit={() => editClicked(s.index)}
                        onMerge={() => mergeClicked(s.index)}
                        onDelete={() => deleteClicked(s.index)}
                        onDuplicate={() => duplicateClicked(s.index)}
                        onRevert={() => revertClicked(s.index)}
                        showVideo={s.index === hoveredIndex}
                        soundEnabled={soundEnabled}
                        onMouseEnter={() => setHoveredIndex(s.index)}
                        onMouseLeave={() => setHoveredIndex(undefined)}
                        inView={inViewRows.includes(row)}
                    />
                )
            })}
        </div>
    )
}

export default Grid
